import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiLock, FiActivity, FiClock } from 'react-icons/fi';
import DbmsPerfAnalysisModal from './DbmsPerfAnalysisModal';
import ConnTabsWindow, { ConnTabPane } from './ConnTabsWindow';
import memoWindow from '../common/memoWindow';
import OwnConnTarget from './OwnConnTarget';
import useOwnConnSession, { ORACLE_CONN } from '../../hooks/useOwnConnSession';
import './OraclePerfAnalysisModal.css';

/**
 * 락 대기 — 메뉴의 **별도 창**(2026-10-07, 사용자 요청). 예전에는 오라클 성능 분석의 하위 탭이었다.
 *
 * 본문(추이 그래프 · 락 종류 필터 · 차단 세션 트리 · SQL 원문)은 `DbmsPerfAnalysisModal` 의 lockwait 탭을
 * **embedded 로 빌려 쓴다** — 오라클 성능 분석 창이 하는 것과 같은 방식이고, 본문을 복제하지 않으므로 고치는 곳이 하나다.
 * 연결과 전담 세션은 이 창이 소유한다(label 'lock-wait' — utils/sessionDisplay 의 OWN_CONN_WINDOW_KEYS).
 *
 * 탭 둘(2026-11-03 시험 피드백 — 오라클 성능 분석의 「실시간 성능 / 성능 이력」 과 같은 구분):
 *   「실시간 락 대기」 = GV$ACTIVE_SESSION_HISTORY(1초 표본, 최근 30분~2시간) · 「락 대기 이력」 = AWR 의 DBA_HIST_ACTIVE_SESS_HISTORY(10초 표본, 일 단위).
 * 탭마다 본문 하나(lockSrc)이고, 한 번 연 탭은 마운트된 채 둔다(숨긴 탭은 자동 갱신만 멈춘다 — paused).
 */
const getLockTabs = (t) => [
    { key: 'rt', label: t('monitoring:perf.lockTab.rt'), icon: FiActivity },
    { key: 'awr', label: t('monitoring:perf.lockTab.awr'), icon: FiClock },
];
/** 연결 하나의 본문 — 창(아래 LockWaitModal)이 DB 연결마다 탭으로 하나씩 띄운다(ConnTabsWindow, 2026-10-14) */
const LockWaitModalBody = ({ isOpen: windowOpen = false, onClose, connections = [], darkMode = false, defaultConnectionId = null, active = true, excludeConnIds = [], onConnectionChange }) => {
    const { t } = useTranslation();
    const mon = useOwnConnSession({
        isOpen: windowOpen, connections, defaultConnectionId, label: 'lock-wait',
        filter: ORACLE_CONN,
    });
    // 연결이 정해지기 전에는 조회하지 않는다(2026-10-14) — 연결 없이 나간 첫 조회에 서버가 예시 데이터를 주어,
    // 창을 열면 예시가 먼저 그려지고 실제 값이 그 위를 덮었다. 고를 연결이 아예 없을 때만 그대로 연다(예시 켜기·오류 안내가 보이게)
    const isOpen = windowOpen && (!!mon.connId || mon.conns.length === 0);
    // 탭 이름이 이 연결을 따라가게 창에 알린다(탭 안에서 연결을 바꿀 수 있다)
    useEffect(() => { if (mon.connId && onConnectionChange) onConnectionChange(mon.connId); }, [mon.connId]); // eslint-disable-line react-hooks/exhaustive-deps
    const [tab, setTab] = useState('rt');
    const [visited, setVisited] = useState(['rt']);
    const pickTab = (key) => { setTab(key); setVisited(prev => (prev.includes(key) ? prev : [...prev, key])); };

    if (!isOpen) return null;

    return (
        <ConnTabPane active={active} toolbar={<OwnConnTarget hideConn
                conns={mon.conns.filter(c => c.id === mon.connId || !excludeConnIds.includes(c.id))} connId={mon.connId}
                onConnChange={(id) => mon.setConnId(id)}
                sessionId={mon.sessionId} session={mon.session}
                sessionError={mon.sessionError} creating={mon.creating}
                onPickSession={(sid, cid) => mon.pickSession(sid, cid)}
            />}>
            <div className="oraperf-tabs" role="tablist">
                {getLockTabs(t).map(tb => {
                    const Icon = tb.icon;
                    return (
                        <button key={tb.key} type="button" role="tab" aria-selected={tab === tb.key}
                            className={`oraperf-tab ${tab === tb.key ? 'active' : ''}`} onClick={() => pickTab(tb.key)}>
                            <Icon size={13} /> {tb.label}
                        </button>
                    );
                })}
                <div className="oraperf-tabs-spacer" />
            </div>
            <div className="oraperf-body">
                {getLockTabs(t).filter(tb => visited.includes(tb.key)).map(tb => (
                    <div key={tb.key} style={{ display: tab === tb.key ? 'contents' : 'none' }}>
                        <DbmsPerfAnalysisModal
                            embedded
                            isOpen={isOpen}
                            onClose={onClose}
                            connections={connections}
                            darkMode={darkMode}
                            activeTab="lockwait"
                            lockSrc={tb.key}
                            paused={tab !== tb.key}
                            selectedConnId={mon.connId}
                            monParams={mon.params}
                            monRun={mon.run}
                        />
                    </div>
                ))}
            </div>
            <div className="oraperf-footer">
                <button type="button" className="app-btn oraperf-btn" onClick={onClose}>{t('monitoring:opa.close')}</button>
            </div>
        </ConnTabPane>
    );
};

LockWaitModalBody.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    connections: PropTypes.arrayOf(PropTypes.object),
    darkMode: PropTypes.bool,
    defaultConnectionId: PropTypes.string,
};


/**
 * 창 — **DB 연결마다 탭**(2026-10-14 요청: 여러 DB 를 동시에 조회). 탭마다 위의 본문 하나(자기 연결의 전담 세션).
 */
const LockWaitModal = (props) => {
    const { t } = useTranslation();
    return (
    <ConnTabsWindow sessionLabel="lock-wait"
        isOpen={props.isOpen} onClose={props.onClose} connections={props.connections}
        defaultConnectionId={props.defaultConnectionId} filter={ORACLE_CONN}
        icon={<FiLock size={14} />} title={t('menu:monitoring.lockWait')} closeLabel={t('monitoring:opa.close')} overlayClassName="oraperf-overlay" className="oraperf-modal" headerClassName="oraperf-header" closeClassName="oraperf-close"
        renderBody={(tab) => <LockWaitModalBody {...props} {...tab} />}
    />
    );
};

// 작업 탭을 오갈 때 Header 가 새 콜백을 넘겨도 본문을 다시 그리지 않는다(common/memoWindow)
export default memoWindow(LockWaitModal);
